/**
 * Convert a decimal coordinate into degrees-minutes or degrees-minutes-seconds format.
 * @param coord decimal coordinate (latitude or longitude)
 * @param type "lat" | "lng"
 * @param showSeconds whether to include seconds
 */
export function LngLatToDMS(coord: number, type: 'lat' | 'lng'): string {
  const absolute = Math.abs(coord);
  const degrees = Math.floor(absolute);
  const minutesFull = (absolute - degrees) * 60;
  const minutes = Math.floor(minutesFull);
  const seconds = (minutesFull - minutes) * 60;

  const direction = type === 'lat' ? (coord >= 0 ? 'N' : 'S') : coord >= 0 ? 'E' : 'W';

  // Match Google Maps precision → 2 decimals for seconds
  return `${degrees}°${minutes}'${seconds.toFixed(2)}"${direction}`;
}

/**
 * Convert a single DMS (degrees–minutes–seconds) coordinate string to decimal degrees.
 *
 * Supports flexible formats such as:
 * - "48°51'23.76\"N"
 * - "2°21'7.92\"E"
 * - "40 44 54 N"
 * - "-73°59'8\"W"
 *
 * @param {string} dms - The DMS coordinate string to convert.
 * @returns {number | null} The decimal degree value, or null if parsing fails.
 */
export function dmsToDecimal(dms: string): number | null {
  if (!dms) return null;

  // Normalize spacing and delimiters
  const normalized = dms
    .trim()
    .toUpperCase()
    .replace(/[^\dNSEW.\-]+/g, ' ') // replace ° ' " and commas with spaces
    .replace(/\s+/g, ' ') // collapse multiple spaces
    .trim();

  // Split into parts
  const parts = normalized.split(' ');

  // Find direction (N, S, E, W)
  const dir = parts.find((p) => /^[NSEW]$/.test(p));
  const nums = parts.filter((p) => /^-?\d+(\.\d+)?$/.test(p)).map(Number);

  if (nums.length === 0) return null;

  const degrees = nums[0];
  const minutes = nums[1] ?? 0;
  const seconds = nums[2] ?? 0;

  let decimal = degrees + minutes / 60 + seconds / 3600;

  if (dir === 'S' || dir === 'W') decimal = -decimal;

  return decimal;
}

/**
 * Parse a full DMS coordinate pair string (latitude + longitude) into decimal degrees.
 *
 * Examples of supported input formats:
 * - "48°51'23.76\"N 2°21'7.92\"E"
 * - "48°51'N, 2°21'E"
 * - "40°44'54.36\"N, 73°59'8.36\"W"
 *
 * @param {string} input - A string containing both latitude and longitude in DMS format.
 * @returns {{ lat: number | null, lng: number | null }} The parsed latitude and longitude in decimal degrees, or null values if parsing fails.
 */
export function parseDMSPair(input: string): { lat: number | null; lng: number | null } {
  // Normalize separators (commas or spaces)
  const parts = input
    .split(/[,;]/)
    .map((p) => p.trim())
    .filter(Boolean);

  if (parts.length === 1) {
    // Try splitting by direction markers if user pasted both together
    const split = parts[0].match(/([0-9.\s°'"]+[NS])\s*([0-9.\s°'"]+[EW])/i);
    if (split) {
      return {
        lat: dmsToDecimal(split[1]),
        lng: dmsToDecimal(split[2]),
      };
    }
    return { lat: null, lng: null };
  }

  // If already clearly two components
  return {
    lat: dmsToDecimal(parts[0]),
    lng: dmsToDecimal(parts[1]),
  };
}

type CoordinateDetection = {
  isLikely: boolean;
  isConfirmed: boolean;
  lat?: number;
  lng?: number;
};

/**
 * Detects and validates decimal coordinate pairs progressively (typed or pasted).
 *
 * Handles partial or full inputs like:
 * - "48.8566, 2.3522"
 * - "48.85 2.35"
 *
 * @param {string} input - The user-provided coordinate string.
 * @returns {{
 *   isLikely: boolean;
 *   isConfirmed: boolean;
 *   lat?: number;
 *   lng?: number;
 * }} Detection result containing confirmation status and parsed lat/lng if valid.
 */
export function detectCoordinatesProgressive(input: string): CoordinateDetection {
  const trimmed = input.trim();

  if (!trimmed) return { isLikely: false, isConfirmed: false };

  // Support various separators: comma, semicolon, or whitespace
  const parts = trimmed
    .split(/[,\s;]+/)
    .map((p) => p.trim())
    .filter(Boolean);

  const partialFloat = /^-?(?:\d+(\.\d*)?|\.\d+)?$/;

  // Single number typed (progressive detection)
  if (parts.length === 1) {
    const val = parts[0];
    const n = parseFloat(val);
    if (partialFloat.test(val) && !Number.isNaN(n) && Math.abs(n) <= 90) {
      return { isLikely: true, isConfirmed: false, lat: n };
    }
    return { isLikely: false, isConfirmed: false };
  }

  // Two numbers (typed or pasted)
  if (parts.length === 2) {
    const [latStr, lngStr] = parts;
    const lat = parseFloat(latStr);
    const lng = parseFloat(lngStr);

    const isLat = partialFloat.test(latStr) && Math.abs(lat) <= 90;
    const isLng = partialFloat.test(lngStr) && Math.abs(lng) <= 180;

    if (isLat && isLng && !Number.isNaN(lat) && !Number.isNaN(lng)) {
      return { isLikely: true, isConfirmed: true, lat, lng };
    }

    // Incomplete but plausible
    if (isLat && (lngStr === '' || partialFloat.test(lngStr))) {
      return { isLikely: true, isConfirmed: false, lat };
    }
  }

  // Too many or invalid parts
  return { isLikely: false, isConfirmed: false };
}
export type ParsedCoordinates = {
  lat: number;
  lng: number;
};

/**
 * Detect and parse either decimal or DMS coordinate input.
 *
 * This function auto-detects whether the input is:
 * - Decimal degrees (e.g. "48.8566, 2.3522")
 * - Degrees–minutes–seconds (e.g. "48°51'23.76\"N 2°21'7.92\"E")
 *
 * It always returns normalized decimal coordinates `{ lat, lng }`
 * if parsing succeeds, or `null` otherwise.
 *
 * @param {string} input - The input text potentially containing coordinates.
 * @returns {ParsedCoordinates | null} Normalized decimal coordinates or null if detection fails.
 */
export function parseAnyCoordinates(input: string): ParsedCoordinates | null {
  if (!input) return null;
  const text = input.trim();

  // Temporarily removed DMS from search bar
  // Quick check: does it look like DMS (has ° or ' or ")
  // const isDMS = /[°'"]/g.test(text);

  // if (isDMS) {
  //   const { lat, lng } = parseDMSPair(text);
  //   if (lat !== null && lng !== null) return { lat, lng };
  // } else {
  // isDecimal
  const detection = detectCoordinatesProgressive(text);
  if (detection.isConfirmed && detection.lat !== undefined && detection.lng !== undefined) {
    return { lat: detection.lat, lng: detection.lng };
  }
  // }

  return null;
}
